import { z } from "zod";

import { frozenText } from "../criticalContent";
import { pageRefSchema, reviewStateSchema } from "../schema";

const sceneIdSchema = z.enum(["M4S1", "M4S2", "M4S3"]);
const taskIdSchema = z.enum(["T11", "T12", "T13", "T14"]);
const nodeIdSchema = z.string().regex(/^M4S[1-3]_(N0[1-6]|GX02_C06)$/);
const claimIdSchema = z.string().regex(/^M4S[1-3]_CLAIM_[A-Z0-9_]+$/);

const textbookSourceSchema = z.object({
  sourceCategory: z.literal("教材正文"),
  provenanceType: z.literal("textbook"),
  sourceRefs: z.tuple([z.literal("SRC_TEXTBOOK_CH3")]),
  citationLabel: z.literal("《中国共产党简史》第三章"),
  pageRefs: z.array(pageRefSchema).min(1),
});

const localExtensionSourceSchema = z.object({
  sourceCategory: z.literal("地方史料拓展"),
  provenanceType: z.literal("local_extension"),
  sourceRefs: z.array(z.enum(["SRC_GX_PARTY_HISTORY_2020", "SRC_GX_WAR_HISTORY"])).min(1),
  citationLabel: z.string().min(1),
  pageRefs: z.tuple([]),
});

const nodeSourceSchema = z.discriminatedUnion("sourceCategory", [
  textbookSourceSchema,
  localExtensionSourceSchema,
]);

const legalAssetKindSchema = z.enum(["programmatic_3d", "programmatic_2_5d", "interface", "media"]);

const assetSuggestionSchema = z
  .object({
    id: z.string().regex(/^AST-M4S[1-3]-SUG-[0-9]{3}$/),
    kind: legalAssetKindSchema,
    title: z.string().min(1),
    usage: z.string().min(1),
    representationLabel: z.enum([
      "复原性创作·非档案影像",
      "教材载述史料·非档案原件",
      "档案素材建议·待来源与权利核验",
    ]),
    rightsPlan: z.enum(["original_procedural", "pending_source_and_rights_review"]),
    provenanceGuard: z.string().min(1),
  })
  .superRefine((asset, context) => {
    if (asset.kind === "media" && asset.rightsPlan !== "pending_source_and_rights_review") {
      context.addIssue({
        code: "custom",
        message: `${asset.id}为媒体素材建议，必须先完成来源与权利核验。`,
        path: ["rightsPlan"],
      });
    }
  });

const interactionSuggestionSchema = z.object({
  mode: z.enum([
    "timeline_scrub",
    "map_layer_compare",
    "source_card_reader",
    "evidence_relation_board",
    "policy_matrix",
    "document_comparison",
    "continuous_archive_timeline",
  ]),
  learnerAction: z.string().min(1),
  persistedState: z.string().min(1),
  evidenceFeedback: z.string().min(1),
  actualInteraction: z.literal(true),
  nonScored: z.literal(true),
  historyConstraint: z.literal("只核验既定史实，不改变历史结果"),
});

const presentationSchema = z.object({
  mode: z.enum([
    "strategic_context_map",
    "solemn_archive_witness",
    "strength_change_ledger",
    "people_war_method_map",
    "production_landscape_timeline",
    "service_archive_reader",
    "governance_policy_board",
    "yanan_spirit_matrix",
    "rear_area_context_map",
    "campaign_loss_evidence_map",
    "democratic_document_wall",
    "local_culture_archive_timeline",
  ]),
  visualSummary: z.string().min(1),
  interaction: interactionSuggestionSchema,
  guardrails: z.array(z.string().min(1)).min(1),
});

export const m4BlueprintNodeSchema = z
  .object({
    id: nodeIdSchema,
    sceneId: sceneIdSchema,
    order: z.number().int().positive(),
    nodeType: z.enum(["lecture", "solemn_archive_witness", "local_extension"]),
    title: z.string().min(1),
    timeAnchor: z.string().min(1),
    estimatedSeconds: z.number().int().positive(),
    claimId: claimIdSchema,
    claim: z.string().min(1),
    bodyZh: z.string().min(1),
    source: nodeSourceSchema,
    assetSuggestions: z.array(assetSuggestionSchema).min(1),
    presentation: presentationSchema,
    sensitivity: z.enum(["standard", "trauma_witness", "sacrifice_witness"]),
    reviewState: reviewStateSchema,
    visibleLabel: z.string().min(1).optional(),
  })
  .superRefine((node, context) => {
    if (!node.id.startsWith(`${node.sceneId}_`)) {
      context.addIssue({
        code: "custom",
        message: `${node.id}必须归属于其sceneId。`,
        path: ["id"],
      });
    }
    if (!node.claimId.startsWith(`${node.sceneId}_`)) {
      context.addIssue({
        code: "custom",
        message: `${node.claimId}必须归属于其sceneId。`,
        path: ["claimId"],
      });
    }

    const isLocalExtension = node.nodeType === "local_extension";
    if (isLocalExtension !== (node.source.provenanceType === "local_extension")) {
      context.addIssue({
        code: "custom",
        message: `${node.id}的节点类型与来源类别不一致。`,
        path: ["source"],
      });
    }
    if (isLocalExtension && node.visibleLabel !== "拓展材料") {
      context.addIssue({
        code: "custom",
        message: `${node.id}必须显示“拓展材料”标签。`,
        path: ["visibleLabel"],
      });
    }
    if (!isLocalExtension && node.source.pageRefs.length === 0) {
      context.addIssue({
        code: "custom",
        message: `${node.id}的教材主张必须登记双页码。`,
        path: ["source", "pageRefs"],
      });
    }

    if (node.sensitivity !== "standard") {
      const requiredGuardrails = [
        "采用庄重史料见证",
        "不作第一人称受难或牺牲重演",
        "不呈现猎奇或伤害特写",
        "不设置战果积分、娱乐化音效或庆祝动画",
        "不使用失败重来叙事",
      ];
      if (
        node.nodeType !== "solemn_archive_witness" ||
        node.presentation.mode !== "solemn_archive_witness"
      ) {
        context.addIssue({
          code: "custom",
          message: `${node.id}必须使用庄重史料见证节点。`,
          path: ["presentation", "mode"],
        });
      }
      for (const guardrail of requiredGuardrails) {
        if (!node.presentation.guardrails.includes(guardrail)) {
          context.addIssue({
            code: "custom",
            message: `${node.id}缺少敏感史料呈现底线：${guardrail}。`,
            path: ["presentation", "guardrails"],
          });
        }
      }
    }
  });

const taskAnchorSchema = z.object({
  taskId: taskIdSchema,
  anchorNodeId: nodeIdSchema,
  relation: z.enum(["fact_recall", "method_match", "document_identification", "policy_claim"]),
});

const extensionLabelSchema = z.object({
  id: z.literal("M4S3_GX02_LABEL"),
  label: z.literal("【广西融入②】"),
  visibleLabel: z.literal("拓展材料"),
  appliesTo: z.tuple([z.literal("M4S3_GX02_C06")]),
});

const narrationTimingSchema = z
  .object({
    mainNarrationSeconds: z.number().int().positive(),
    extensionNarrationSeconds: z.number().int().nonnegative(),
    totalNarrationSeconds: z.number().int().positive(),
  })
  .superRefine((timing, context) => {
    if (
      timing.totalNarrationSeconds !==
      timing.mainNarrationSeconds + timing.extensionNarrationSeconds
    ) {
      context.addIssue({
        code: "custom",
        message: "总讲授时长必须等于教材主体与地方拓展时长之和。",
        path: ["totalNarrationSeconds"],
      });
    }
  });

export const m4SceneBlueprintSchema = z
  .object({
    id: sceneIdSchema,
    order: z.number().int().min(1).max(3),
    title: z.string().min(1),
    identity: z.literal("抗战史料研学员"),
    timeLabel: z.string().min(1),
    mainVenue: z.string().min(1),
    archiveSlices: z.array(z.string().min(1)),
    pageRefs: z.array(pageRefSchema).length(1),
    taskIds: z.array(taskIdSchema).min(1).max(2),
    taskAnchors: z.array(taskAnchorSchema).min(1).max(2),
    sequence: z.array(z.string().min(1)).min(3),
    nodes: z.array(m4BlueprintNodeSchema).min(3).max(6),
    extensionLabel: extensionLabelSchema.optional(),
    gamificationPolicy: z.literal("forbidden"),
    timing: narrationTimingSchema,
    reviewState: reviewStateSchema,
  })
  .superRefine((scene, context) => {
    const nodeIds = scene.nodes.map((node) => node.id);
    const claimIds = scene.nodes.map((node) => node.claimId);
    const textbookSeconds = scene.nodes
      .filter((node) => node.source.provenanceType === "textbook")
      .reduce((total, node) => total + node.estimatedSeconds, 0);
    const extensionSeconds = scene.nodes
      .filter((node) => node.source.provenanceType === "local_extension")
      .reduce((total, node) => total + node.estimatedSeconds, 0);

    if (scene.nodes.some((node) => node.sceneId !== scene.id)) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}只能包含本场景节点。`,
        path: ["nodes"],
      });
    }
    if (new Set(nodeIds).size !== nodeIds.length || new Set(claimIds).size !== claimIds.length) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}节点ID和claimId必须唯一。`,
        path: ["nodes"],
      });
    }
    if (scene.nodes.some((node, index) => node.order !== (index + 1) * 10)) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}节点必须按10递增声明。`,
        path: ["nodes"],
      });
    }
    if (
      scene.taskAnchors.map((anchor) => anchor.taskId).join("|") !== scene.taskIds.join("|") ||
      scene.taskAnchors.some((anchor) => !nodeIds.includes(anchor.anchorNodeId))
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}每项正式任务必须按原顺序锚定本场景知识节点。`,
        path: ["taskAnchors"],
      });
    }
    if (
      scene.timing.mainNarrationSeconds !== textbookSeconds ||
      scene.timing.extensionNarrationSeconds !== extensionSeconds
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}讲授时长必须等于节点时长汇总。`,
        path: ["timing"],
      });
    }
  });

const reviewNoteSchema = z.object({
  id: z.enum(["M4_REVIEW_C05_REPLACEMENT", "M4_REVIEW_C06_REPLACEMENT"]),
  scope: z.string().min(1),
  state: z.literal("open"),
  differenceFromOriginal: z.string().min(1),
  implementationDisposition: z.string().min(1),
});

export const m4ExpectedSequences = {
  M4S1: ["M4S1_N01", "M4S1_N02", "M4S1_N03", "M4S1_N04", "M4S1_N05", "M4S1_N06", "T11", "T12"],
  M4S2: ["M4S2_N01", "M4S2_N02", "M4S2_N03", "M4S2_N04", "T13"],
  M4S3: ["M4S3_N01", "M4S3_N02", "M4S3_N03", "M4S3_GX02_LABEL", "M4S3_GX02_C06", "T14"],
} as const;

const expectedTaskMap = {
  M4S1: ["T11", "T12"],
  M4S2: ["T13"],
  M4S3: ["T14"],
} as const;

const requiredSolemnWitnessNodeIds = ["M4S1_N02", "M4S1_N05", "M4S1_N06"] as const;

const oldGuangxi02Marker = "在国统区抗日民主运动日益高涨的背景下，广西的抗战文化运动也蓬勃发展";

export const m4BlueprintSchema = z
  .object({
    id: z.literal("M4"),
    version: z.literal("1.0.0"),
    title: z.literal("巩固抗日根据地和推进抗日民主运动"),
    identity: z.literal("抗战史料研学员"),
    printPageRange: z.literal("91—97"),
    pdfPageRange: z.literal("111—117"),
    sourceRefs: z.tuple([z.literal("SRC_TEXTBOOK_CH3")]),
    sceneIds: z.tuple([z.literal("M4S1"), z.literal("M4S2"), z.literal("M4S3")]),
    taskIds: z.tuple([z.literal("T11"), z.literal("T12"), z.literal("T13"), z.literal("T14")]),
    scenes: z.array(m4SceneBlueprintSchema).length(3),
    timing: narrationTimingSchema,
    reviewState: reviewStateSchema,
    reviewNotes: z.array(reviewNoteSchema).length(2),
  })
  .superRefine((blueprint, context) => {
    if (
      blueprint.scenes.some(
        (scene, index) => scene.id !== blueprint.sceneIds[index] || scene.order !== index + 1,
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "模块四必须固定保留M4S1—M4S3三个场景及其顺序。",
        path: ["scenes"],
      });
    }

    for (const [sceneIndex, scene] of blueprint.scenes.entries()) {
      const expectedSequence = m4ExpectedSequences[scene.id];
      const expectedTasks = expectedTaskMap[scene.id];
      if (scene.sequence.join("|") !== expectedSequence.join("|")) {
        context.addIssue({
          code: "custom",
          message: `${scene.id}的主体、拓展与任务顺序不得改变。`,
          path: ["scenes", sceneIndex, "sequence"],
        });
      }
      if (scene.taskIds.join("|") !== expectedTasks.join("|")) {
        context.addIssue({
          code: "custom",
          message: `${scene.id}的正式任务映射不得改变。`,
          path: ["scenes", sceneIndex, "taskIds"],
        });
      }
    }

    const allNodes = blueprint.scenes.flatMap((scene) => scene.nodes);
    const allNodeIds = allNodes.map((node) => node.id);
    const allClaimIds = allNodes.map((node) => node.claimId);
    if (
      new Set(allNodeIds).size !== allNodeIds.length ||
      new Set(allClaimIds).size !== allClaimIds.length
    ) {
      context.addIssue({
        code: "custom",
        message: "模块四全局节点ID和claimId必须唯一。",
        path: ["scenes"],
      });
    }

    const mainSeconds = blueprint.scenes.reduce(
      (total, scene) => total + scene.timing.mainNarrationSeconds,
      0,
    );
    const extensionSeconds = blueprint.scenes.reduce(
      (total, scene) => total + scene.timing.extensionNarrationSeconds,
      0,
    );
    if (
      blueprint.timing.mainNarrationSeconds !== mainSeconds ||
      blueprint.timing.extensionNarrationSeconds !== extensionSeconds ||
      blueprint.timing.totalNarrationSeconds !== mainSeconds + extensionSeconds
    ) {
      context.addIssue({
        code: "custom",
        message: "模块四时长必须等于三个场景的节点时长汇总。",
        path: ["timing"],
      });
    }

    const m4s1 = blueprint.scenes.find((scene) => scene.id === "M4S1");
    const solemnNodeIds =
      m4s1?.nodes.filter((node) => node.sensitivity !== "standard").map((node) => node.id) ?? [];
    if (solemnNodeIds.join("|") !== requiredSolemnWitnessNodeIds.join("|")) {
      context.addIssue({
        code: "custom",
        message: "潘家峪、狼牙山、刘老庄必须且只能作为三处庄重史料见证节点。",
        path: ["scenes", 0, "nodes"],
      });
    }

    const m4s2 = blueprint.scenes.find((scene) => scene.id === "M4S2");
    const m4s2Body = m4s2?.nodes.map((node) => node.bodyZh).join("\n\n") ?? "";
    if (m4s2Body !== frozenText.m4s2C05 || m4s2?.nodes.length !== 4) {
      context.addIssue({
        code: "custom",
        message: "M4S2主体讲授必须且只能按顺序加载C05四段全文。",
        path: ["scenes", 1, "nodes"],
      });
    }
    if (m4s2Body.includes("干部学校") || m4s2Body.includes("延安自然科学院")) {
      context.addIssue({
        code: "custom",
        message: "C05已删除的干部学校/延安自然科学院旧段不得进入M4S2。",
        path: ["scenes", 1, "nodes"],
      });
    }

    const m4s3 = blueprint.scenes.find((scene) => scene.id === "M4S3");
    const labels = blueprint.scenes.flatMap((scene) =>
      scene.extensionLabel ? [scene.extensionLabel] : [],
    );
    const localNodes = allNodes.filter((node) => node.source.provenanceType === "local_extension");
    if (
      labels.length !== 1 ||
      labels[0]?.label !== "【广西融入②】" ||
      m4s3?.extensionLabel?.id !== "M4S3_GX02_LABEL" ||
      localNodes.length !== 1 ||
      localNodes[0]?.id !== "M4S3_GX02_C06" ||
      localNodes[0]?.bodyZh !== frozenText.gx02C06
    ) {
      context.addIssue({
        code: "custom",
        message: "M4S3必须只加载单一广西融入②标签及C06新稿全文。",
        path: ["scenes", 2],
      });
    }

    const serialized = JSON.stringify(blueprint.scenes);
    if (
      serialized.includes(oldGuangxi02Marker) ||
      serialized.includes("广西融入③") ||
      serialized.includes("广西融入④")
    ) {
      context.addIssue({
        code: "custom",
        message: "旧广西融入②及已删除的③、④不得以任何节点或隐藏内容加载。",
        path: ["scenes"],
      });
    }

    const reviewNoteIds = blueprint.reviewNotes.map((note) => note.id);
    if (
      new Set(reviewNoteIds).size !== 2 ||
      !reviewNoteIds.includes("M4_REVIEW_C05_REPLACEMENT") ||
      !reviewNoteIds.includes("M4_REVIEW_C06_REPLACEMENT")
    ) {
      context.addIssue({
        code: "custom",
        message: "模块四只登记C05、C06两项相对原方案的已确认差异。",
        path: ["reviewNotes"],
      });
    }
  });

export type M4BlueprintNode = z.infer<typeof m4BlueprintNodeSchema>;
export type M4SceneBlueprint = z.infer<typeof m4SceneBlueprintSchema>;
export type M4Blueprint = z.infer<typeof m4BlueprintSchema>;

const splitFourParagraphs = (text: string): [string, string, string, string] => {
  const paragraphs = text.split("\n\n");
  if (paragraphs.length !== 4) {
    throw new Error(`冻结文本段落数应为4，实际为${paragraphs.length}。`);
  }
  return paragraphs as [string, string, string, string];
};

const c05Paragraphs = splitFourParagraphs(frozenText.m4s2C05);

const textbookSource = (print: string, pdf: string): M4BlueprintNode["source"] => ({
  sourceCategory: "教材正文",
  provenanceType: "textbook",
  sourceRefs: ["SRC_TEXTBOOK_CH3"],
  citationLabel: "《中国共产党简史》第三章",
  pageRefs: [{ print, pdf }],
});

const localExtensionSource = (): M4BlueprintNode["source"] => ({
  sourceCategory: "地方史料拓展",
  provenanceType: "local_extension",
  sourceRefs: ["SRC_GX_PARTY_HISTORY_2020", "SRC_GX_WAR_HISTORY"],
  citationLabel: "中共广西区委党史研究室《旗帜不倒 抗战到底》；广西政协文史资料《广西与抗日战争》",
  pageRefs: [],
});

const assetSuggestion = (
  id: string,
  kind: z.infer<typeof legalAssetKindSchema>,
  title: string,
  usage: string,
  archiveMedia = false,
): M4BlueprintNode["assetSuggestions"][number] => ({
  id,
  kind,
  title,
  usage,
  representationLabel: archiveMedia ? "档案素材建议·待来源与权利核验" : "复原性创作·非档案影像",
  rightsPlan: archiveMedia ? "pending_source_and_rights_review" : "original_procedural",
  provenanceGuard: archiveMedia
    ? "正式接入前核验原始出处、年代、人物/地点标注、授权链与可用范围。"
    : "程序化原创画面必须显示“复原性创作·非档案影像”，不得冒充历史照片或真实现场。",
});

const standardGuardrails = [
  "不模拟学习者改变历史结果",
  "不以伤亡数字、武器操作或战果设置积分",
  "复原画面与档案素材使用不同可见标签",
];

const solemnGuardrails = [
  "采用庄重史料见证",
  "不作第一人称受难或牺牲重演",
  "不呈现猎奇或伤害特写",
  "不设置战果积分、娱乐化音效或庆祝动画",
  "不使用失败重来叙事",
];

export const m4Blueprint: M4Blueprint = {
  id: "M4",
  version: "1.0.0",
  title: "巩固抗日根据地和推进抗日民主运动",
  identity: "抗战史料研学员",
  printPageRange: "91—97",
  pdfPageRange: "111—117",
  sourceRefs: ["SRC_TEXTBOOK_CH3"],
  sceneIds: ["M4S1", "M4S2", "M4S3"],
  taskIds: ["T11", "T12", "T13", "T14"],
  scenes: [
    {
      id: "M4S1",
      order: 1,
      title: "敌后军民艰苦的反“扫荡”、反“清乡”斗争",
      identity: "抗战史料研学员",
      timeLabel: "1941年1月—1943年3月",
      mainVenue: "华北、华中敌后根据地",
      archiveSlices: ["潘家峪", "狼牙山", "刘老庄"],
      pageRefs: [{ print: "91—93", pdf: "111—113" }],
      taskIds: ["T11", "T12"],
      taskAnchors: [
        { taskId: "T11", anchorNodeId: "M4S1_N02", relation: "fact_recall" },
        { taskId: "T12", anchorNodeId: "M4S1_N04", relation: "method_match" },
      ],
      sequence: [...m4ExpectedSequences.M4S1],
      nodes: [
        {
          id: "M4S1_N01",
          sceneId: "M4S1",
          order: 10,
          nodeType: "lecture",
          title: "敌后抗战进入最困难时期",
          timeAnchor: "1941—1942年",
          estimatedSeconds: 40,
          claimId: "M4S1_CLAIM_HARDEST_PERIOD",
          claim:
            "1941年至1942年是中国敌后抗战最为困难的时期，中国战场成为世界反法西斯战争的东方主战场。",
          bodyZh:
            "1941年至1942年，是中国敌后抗战最为困难的时期。此时，德、日等法西斯势力的侵略气焰达到顶点。1941年12月，日军偷袭美国海军基地珍珠港，挑起太平洋战争。中国战场成为世界反法西斯战争的东方主战场。",
          source: textbookSource("91", "111"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S1-SUG-001",
              "programmatic_2_5d",
              "1941—1942年世界与中国战场态势图",
              "用时间轴与分层地图交代世界战局、东方主战场和敌后根据地困难，不将跨地域事件拼成同一现场。",
            ),
          ],
          presentation: {
            mode: "strategic_context_map",
            visualSummary: "以1941—1942年时间轴联动世界战局与中国敌后态势图。",
            interaction: {
              mode: "timeline_scrub",
              learnerAction: "拖动年份节点，查看世界战局与敌后抗战困难的同步变化。",
              persistedState: "记录已核验的时间节点与打开过的来源卡。",
              evidenceFeedback: "系统只反馈节点间的史实关系，并展示对应教材双页码。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          sensitivity: "standard",
          reviewState: "pending_review",
        },
        {
          id: "M4S1_N02",
          sceneId: "M4S1",
          order: 20,
          nodeType: "solemn_archive_witness",
          title: "潘家峪惨案史料见证",
          timeAnchor: "1941年1月下旬",
          estimatedSeconds: 45,
          claimId: "M4S1_CLAIM_PANJIAYU_MASSACRE",
          claim:
            "日军在冀东丰润潘家峪制造惨案，是其对抗日根据地实行毁灭性“扫荡”和“三光”政策的罪证。",
          bodyZh:
            "日本侵略者对各抗日根据地发动空前残酷的毁灭性的“扫荡”和“清乡”，实行野蛮的烧光、杀光、抢光的“三光”政策，使用毒气和细菌武器，制造无人区，企图摧毁敌后抗日军民的生存条件。1941年1月下旬，日军1500余人“扫荡”冀东丰润的潘家峪时，将全村男女老幼驱赶到一个大院内，以机枪扫射，屠杀群众约1300人，烧毁房屋千余间，造成惨绝人寰的“潘家峪惨案”。",
          source: textbookSource("92", "112"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S1-SUG-002",
              "media",
              "潘家峪惨案档案与遗址史料建议",
              "采用遗址远景、史料题名、时间地点和教材引文构成低刺激证据卡；不使用受难重演画面。",
              true,
            ),
          ],
          presentation: {
            mode: "solemn_archive_witness",
            visualSummary: "先显示内容提醒，再以遗址远景、时间地点和来源卡完成庄重见证。",
            interaction: {
              mode: "source_card_reader",
              learnerAction:
                "依次展开地点、时间、暴行政策和教材出处四张证据卡，并可切换低刺激纯文字模式。",
              persistedState: "记录核心证据卡已阅读状态，不记录速度、连击或得分。",
              evidenceFeedback: "完成阅读后显示史实关联与教材页码，不播放胜负反馈。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: solemnGuardrails,
          },
          sensitivity: "trauma_witness",
          reviewState: "pending_review",
        },
        {
          id: "M4S1_N03",
          sceneId: "M4S1",
          order: 30,
          nodeType: "lecture",
          title: "敌后根据地力量承受严重损失",
          timeAnchor: "1941—1942年",
          estimatedSeconds: 30,
          claimId: "M4S1_CLAIM_BASE_AREA_CONTRACTION",
          claim: "在严重困难中，八路军、新四军兵力和抗日根据地人口规模都显著下降。",
          bodyZh:
            "在这种情况下，八路军、新四军由50万人减为约40万人，抗日根据地面积缩小，总人口由1亿人减少到5000万人以下。",
          source: textbookSource("92", "112"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S1-SUG-003",
              "interface",
              "根据地力量变化证据账页",
              "用不带胜负色彩的比例尺与数字账页呈现兵力、面积和人口变化，并保留来源抽屉。",
            ),
          ],
          presentation: {
            mode: "strength_change_ledger",
            visualSummary: "使用前后对照账页呈现力量与生存空间的收缩。",
            interaction: {
              mode: "evidence_relation_board",
              learnerAction: "将兵力、根据地面积和人口三类指标连到同一困难阶段。",
              persistedState: "保存已建立的证据关系，供场景结语回看。",
              evidenceFeedback: "连线后解释这些数据共同说明的困难，不进行战果排名。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          sensitivity: "standard",
          reviewState: "pending_review",
        },
        {
          id: "M4S1_N04",
          sceneId: "M4S1",
          order: 40,
          nodeType: "lecture",
          title: "人民战争战略战术",
          timeAnchor: "1941—1942年反“扫荡”、反“清乡”斗争",
          estimatedSeconds: 45,
          claimId: "M4S1_CLAIM_PEOPLE_WAR_METHODS",
          claim:
            "敌后军民因地制宜创造多种歼敌方法和武装工作队等斗争形式，发展了人民战争的战略战术。",
          bodyZh:
            "在反“扫荡”、反“清乡”斗争中，敌后军民创造了很多极为有效的歼敌方法，如麻雀战、地道战、地雷战、破袭战、水上游击战等，还创造了建立武装工作队等斗争形式，发展了人民战争的战略战术。",
          source: textbookSource("92", "112"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S1-SUG-004",
              "programmatic_3d",
              "敌后战法空间关系沙盘",
              "以抽象地形和路径图解释不同战法适用空间，不提供武器操控或消灭敌人的游戏目标。",
            ),
          ],
          presentation: {
            mode: "people_war_method_map",
            visualSummary: "将五类战法作为地形、隐蔽、爆破、交通与水域证据层呈现。",
            interaction: {
              mode: "map_layer_compare",
              learnerAction: "切换地形与目标图层，比较各战法的条件和主要作用。",
              persistedState: "记录学习者完成比较的战法图层，作为T12前置证据。",
              evidenceFeedback: "显示战法与描述的史实对应关系，不模拟射击、爆炸或伤亡。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          sensitivity: "standard",
          reviewState: "pending_review",
        },
        {
          id: "M4S1_N05",
          sceneId: "M4S1",
          order: 50,
          nodeType: "solemn_archive_witness",
          title: "狼牙山五壮士史料见证",
          timeAnchor: "1941年9月",
          estimatedSeconds: 45,
          claimId: "M4S1_CLAIM_LANGYASHAN_FIVE_HEROES",
          claim:
            "狼牙山五壮士为掩护党政机关和群众转移，据险抵抗直至弹尽，展现了敌后军民的英勇牺牲。",
          bodyZh:
            "1941年9月，在冀西狼牙山地区，八路军战士马宝玉、胡德林、胡福才、宋学义、葛振林，为掩护党政机关和群众转移，主动把日伪军吸引到自己身边，一步步退到悬崖绝壁，据险抵抗。在打完最后一粒子弹后，他们毅然砸枪跳崖。人们称他们为“狼牙山五壮士”。",
          source: textbookSource("93", "113"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S1-SUG-005",
              "media",
              "狼牙山纪念地与人物姓名史料建议",
              "以纪念地远景、五人姓名和群众转移路线构成证据界面，不重演跳崖过程。",
              true,
            ),
          ],
          presentation: {
            mode: "solemn_archive_witness",
            visualSummary: "以姓名、时间、地点、掩护对象和纪念地远景组织庄重见证。",
            interaction: {
              mode: "source_card_reader",
              learnerAction: "沿群众转移路线依次核验掩护行动、五人姓名与教材出处。",
              persistedState: "仅记录证据卡已阅读状态，不记录战斗表现。",
              evidenceFeedback: "完成后展示“掩护群众转移”的史实主线和来源，不使用庆祝动画。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: solemnGuardrails,
          },
          sensitivity: "sacrifice_witness",
          reviewState: "pending_review",
        },
        {
          id: "M4S1_N06",
          sceneId: "M4S1",
          order: 60,
          nodeType: "solemn_archive_witness",
          title: "刘老庄连史料见证",
          timeAnchor: "1943年3月",
          estimatedSeconds: 35,
          claimId: "M4S1_CLAIM_LIULAOZHUANG_COMPANY",
          claim: "1943年3月，新四军“刘老庄连”在与敌人战斗中全部壮烈牺牲。",
          bodyZh: "1943年3月，新四军“刘老庄连”在与敌人战斗中全部壮烈牺牲。",
          source: textbookSource("93", "113"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S1-SUG-006",
              "media",
              "刘老庄连纪念史料建议",
              "采用纪念设施远景、连队称谓、时间与教材引文，避免战斗过程视觉化重演。",
              true,
            ),
          ],
          presentation: {
            mode: "solemn_archive_witness",
            visualSummary: "以短时静默、纪念地远景和教材引文完成庄重见证。",
            interaction: {
              mode: "source_card_reader",
              learnerAction: "展开时间、连队称谓和教材出处三项史料卡，可选择纯文字低刺激模式。",
              persistedState: "记录核心史实已阅读，不记录伤亡数或战果。",
              evidenceFeedback: "系统以来源回看结束节点，不设置胜负、失败重来或娱乐化反馈。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: solemnGuardrails,
          },
          sensitivity: "sacrifice_witness",
          reviewState: "pending_review",
        },
      ],
      gamificationPolicy: "forbidden",
      timing: {
        mainNarrationSeconds: 240,
        extensionNarrationSeconds: 0,
        totalNarrationSeconds: 240,
      },
      reviewState: "pending_review",
    },
    {
      id: "M4S2",
      order: 2,
      title: "大生产运动和抗日根据地的建设",
      identity: "抗战史料研学员",
      timeLabel: "1939年2月—1944年9月",
      mainVenue: "陕甘宁边区，以南泥湾、延安及根据地治理沙盘为主要场域",
      archiveSlices: [],
      pageRefs: [{ print: "93—96", pdf: "113—116" }],
      taskIds: ["T13"],
      taskAnchors: [
        { taskId: "T13", anchorNodeId: "M4S2_N02", relation: "document_identification" },
      ],
      sequence: [...m4ExpectedSequences.M4S2],
      nodes: [
        {
          id: "M4S2_N01",
          sceneId: "M4S2",
          order: 10,
          nodeType: "lecture",
          title: "大生产运动与南泥湾军垦屯田",
          timeAnchor: "1939年2月—1941年春",
          estimatedSeconds: 60,
          claimId: "M4S2_CLAIM_PRODUCTION_MOVEMENT",
          claim: "大生产运动以“发展经济，保障供给”为总方针，第三五九旅在南泥湾实行军垦屯田。",
          bodyZh: c05Paragraphs[0],
          source: textbookSource("93", "113"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S2-SUG-001",
              "programmatic_3d",
              "南泥湾生产景观时序复原",
              "以季节和土地利用变化解释军垦屯田，不把艺术化复原当作档案影像。",
            ),
          ],
          presentation: {
            mode: "production_landscape_timeline",
            visualSummary: "从1939年号召到1941年春军垦屯田，呈现生产自救的因果链。",
            interaction: {
              mode: "timeline_scrub",
              learnerAction: "拖动时间轴查看“自己动手”、生产自救与南泥湾军垦的先后关系。",
              persistedState: "保存已核验的政策与行动节点。",
              evidenceFeedback: "反馈节点顺序和“发展经济，保障供给”的政策主线。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          sensitivity: "standard",
          reviewState: "pending_review",
        },
        {
          id: "M4S2_N02",
          sceneId: "M4S2",
          order: 20,
          nodeType: "lecture",
          title: "领导示范、张思德与《为人民服务》",
          timeAnchor: "1943年—1944年9月",
          estimatedSeconds: 80,
          claimId: "M4S2_CLAIM_SERVE_THE_PEOPLE",
          claim: "中央领导人带头生产；张思德牺牲后，毛泽东在追悼会上发表《为人民服务》。",
          bodyZh: c05Paragraphs[1],
          source: textbookSource("93—94", "113—114"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S2-SUG-002",
              "media",
              "机关干部纺线与张思德史料建议",
              "将教材图片说明、人物事件与《为人民服务》引文分层呈现；引文逐字显示出处。",
              true,
            ),
          ],
          presentation: {
            mode: "service_archive_reader",
            visualSummary: "以生产示范、张思德牺牲和讲演引文三层证据串联为人民服务宗旨。",
            interaction: {
              mode: "source_card_reader",
              learnerAction: "依次展开生产示范、人物事件和讲演引文，点击引文查看对应教材页码。",
              persistedState: "记录引文与人物事件的来源核验状态。",
              evidenceFeedback: "反馈《为人民服务》与张思德追悼会的对应关系，为T13提供证据回看。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: [
              ...standardGuardrails,
              "直接引文逐字呈现并显示出处",
              "不合成真实领导人的未见于史料的语音、对话或口型",
            ],
          },
          sensitivity: "standard",
          reviewState: "pending_review",
        },
        {
          id: "M4S2_N03",
          sceneId: "M4S2",
          order: 30,
          nodeType: "lecture",
          title: "巩固根据地的十大政策",
          timeAnchor: "抗日根据地困难时期",
          estimatedSeconds: 30,
          claimId: "M4S2_CLAIM_TEN_POLICIES",
          claim:
            "精兵简政、统一领导、拥政爱民、“三三制”、减租减息等政策帮助根据地克服困难、巩固发展。",
          bodyZh: c05Paragraphs[2],
          source: textbookSource("95—96", "115—116"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S2-SUG-003",
              "interface",
              "根据地建设政策关系板",
              "以政策—问题—作用三列关系板呈现确认稿所列政策，不扩写未核定细节。",
            ),
          ],
          presentation: {
            mode: "governance_policy_board",
            visualSummary: "把确认稿列举的政策与克服困难、巩固根据地的共同作用相连。",
            interaction: {
              mode: "policy_matrix",
              learnerAction: "选择政策卡，查看其在确认稿中的原句位置和共同作用。",
              persistedState: "记录已核验的政策卡，不生成额外计分任务。",
              evidenceFeedback: "系统返回确认稿原句与双页码，避免把政策作用过度细分。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          sensitivity: "standard",
          reviewState: "pending_review",
        },
        {
          id: "M4S2_N04",
          sceneId: "M4S2",
          order: 40,
          nodeType: "lecture",
          title: "延安精神的主要内容",
          timeAnchor: "抗日根据地建设经验的积累",
          estimatedSeconds: 70,
          claimId: "M4S2_CLAIM_YANAN_SPIRIT",
          claim: "延安精神集中体现坚定正确的政治方向、实事求是、为人民服务和自力更生艰苦奋斗。",
          bodyZh: c05Paragraphs[3],
          source: textbookSource("96", "116"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S2-SUG-004",
              "programmatic_2_5d",
              "延安精神四维证据矩阵",
              "将四项主要内容与前述生产、宗旨和根据地建设证据关联，不制作口号式漂浮特效。",
            ),
          ],
          presentation: {
            mode: "yanan_spirit_matrix",
            visualSummary: "用四维矩阵回扣本场景史料，使精神内涵由具体实践证据支撑。",
            interaction: {
              mode: "evidence_relation_board",
              learnerAction: "把场景中的生产、服务、治理证据连到延安精神四项主要内容。",
              persistedState: "保存完成的证据—精神内涵关系，供模块复盘读取。",
              evidenceFeedback: "反馈每条关系所依据的C05原文，不新增未经确认的表述。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          sensitivity: "standard",
          reviewState: "pending_review",
        },
      ],
      gamificationPolicy: "forbidden",
      timing: {
        mainNarrationSeconds: 240,
        extensionNarrationSeconds: 0,
        totalNarrationSeconds: 240,
      },
      reviewState: "pending_review",
    },
    {
      id: "M4S3",
      order: 3,
      title: "国民党统治区的抗日民主运动",
      identity: "抗战史料研学员",
      timeLabel: "主线1943年—1944年10月；广西节点1938年10月后、1941年1月、1944年冬",
      mainVenue: "重庆及豫湘桂战场",
      archiveSlices: ["桂林", "昭平县黄姚"],
      pageRefs: [{ print: "96—97", pdf: "116—117" }],
      taskIds: ["T14"],
      taskAnchors: [{ taskId: "T14", anchorNodeId: "M4S3_N03", relation: "policy_claim" }],
      sequence: [...m4ExpectedSequences.M4S3],
      nodes: [
        {
          id: "M4S3_N01",
          sceneId: "M4S3",
          order: 10,
          nodeType: "lecture",
          title: "中国共产党在国民党统治区开展工作",
          timeAnchor: "全民族抗战时期",
          estimatedSeconds: 45,
          claimId: "M4S3_CLAIM_REAR_AREA_WORK",
          claim:
            "中国共产党在领导敌后抗战和沦陷区斗争的同时，也在国民党统治区开展了大量卓有成效的工作。",
          bodyZh:
            "中国共产党集中力量在敌后发动抗日游击战争、建设抗日民主根据地和领导沦陷区人民开展多种形式的反日斗争的同时，也在国民党统治区（习惯上称“大后方”）开展了大量卓有成效的工作。",
          source: textbookSource("96", "116"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S3-SUG-001",
              "programmatic_2_5d",
              "敌后—大后方工作关系图",
              "用双场域关系图呈现党在不同区域的工作，不把敌后、重庆与广西档案切片合成同一现场。",
            ),
          ],
          presentation: {
            mode: "rear_area_context_map",
            visualSummary: "以敌后、沦陷区和国民党统治区三层地图解释工作的并行关系。",
            interaction: {
              mode: "map_layer_compare",
              learnerAction: "切换区域图层，查看不同区域的工作类型及对应教材原句。",
              persistedState: "保存已查看的区域与来源卡。",
              evidenceFeedback: "反馈各区域在教材叙事中的区别，不把离散地点并置为同一事件。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          sensitivity: "standard",
          reviewState: "pending_review",
        },
        {
          id: "M4S3_N02",
          sceneId: "M4S3",
          order: 20,
          nodeType: "lecture",
          title: "敌后攻势与豫湘桂大溃退的对照",
          timeAnchor: "1943年—1944年冬",
          estimatedSeconds: 80,
          claimId: "M4S3_CLAIM_HENAN_HUNAN_GUANGXI_RETREAT",
          claim:
            "1943年起人民军队在一些地区展开攻势，而1944年正面战场豫湘桂大溃退加剧了实行民主政治、增强团结抗战力量的社会要求。",
          bodyZh:
            "从1943年起，党领导的人民军队在一些地区开始了对日伪军的攻势作战。同一时期，中国正面战场却出现了豫湘桂大溃退。在1944年春季至冬季的8个月中，丢失了146座城市、总计20多万平方公里国土，6000多万同胞沦于日本侵略者铁蹄之下。人民从事实中得出结论：国民党统治集团不能担负起争取抗战胜利的任务，不能维护中国的独立、推动经济的发展，只能成为中国进步的障碍。废除国民党一党专政，实行民主政治，以增强团结抗战的力量，已成为人们越来越强烈的要求。",
          source: textbookSource("96—97", "116—117"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S3-SUG-002",
              "interface",
              "1943—1944年战场与民主诉求证据对照",
              "把敌后攻势、正面战场退却数据和民主诉求分为三层证据，避免用娱乐化损失计数器。",
            ),
          ],
          presentation: {
            mode: "campaign_loss_evidence_map",
            visualSummary: "以地图、数据账页和民主诉求文本建立因果证据链。",
            interaction: {
              mode: "evidence_relation_board",
              learnerAction: "选择战场数据证据，连接到教材所述社会判断和民主政治要求。",
              persistedState: "保存已完成的证据关系和来源页码。",
              evidenceFeedback: "反馈教材中的因果表述，不把领土与民众苦难转化为游戏资源。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          sensitivity: "standard",
          reviewState: "pending_review",
        },
        {
          id: "M4S3_N03",
          sceneId: "M4S3",
          order: 30,
          nodeType: "lecture",
          title: "民主联合政府主张",
          timeAnchor: "1944年9月—10月10日",
          estimatedSeconds: 55,
          claimId: "M4S3_CLAIM_DEMOCRATIC_COALITION_GOVERNMENT",
          claim:
            "林伯渠根据党中央指示提出建立各抗日党派民主联合政府的主张，随后中国民主同盟公开响应民主政治要求。",
          bodyZh:
            "1944年9月，林伯渠根据党中央指示，在国民参政会上正式提出立即结束国民党一党统治、建立各抗日党派民主联合政府的主张。中国共产党的这个主张，在国内外引起强烈反响。中国民主同盟在10月10日发表《对抗战最后阶段的政治主张》，要求立即结束一党专政，建立各党派联合政权，实行民主政治。",
          source: textbookSource("97", "117"),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S3-SUG-003",
              "interface",
              "1944年民主政治文献墙",
              "以文献题名、时间、提出者和主张摘要构成双文献对照，不伪造历史文件原件外观。",
            ),
          ],
          presentation: {
            mode: "democratic_document_wall",
            visualSummary: "并列呈现9月联合政府主张与10月10日民主同盟政治主张。",
            interaction: {
              mode: "document_comparison",
              learnerAction: "选择两份文献的时间、主体和共同诉求，展开教材原句与页码。",
              persistedState: "保存已核验的文献要素，作为T14答题前证据。",
              evidenceFeedback: "反馈联合政府主张的完整表述及两份文献的时间先后。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          sensitivity: "standard",
          reviewState: "pending_review",
        },
        {
          id: "M4S3_GX02_C06",
          sceneId: "M4S3",
          order: 40,
          nodeType: "local_extension",
          title: "桂林抗战文化运动与“文化城”",
          timeAnchor: "1938年10月后—1944年冬（离散广西档案切片）",
          estimatedSeconds: 200,
          claimId: "M4S3_CLAIM_GX_CULTURAL_RESISTANCE_C06",
          claim:
            "在中国共产党领导和统一战线工作推动下，桂林抗战文化运动持续发展，并通过桂林、昭平黄姚等节点服务广西和全国抗战。",
          bodyZh: frozenText.gx02C06,
          source: localExtensionSource(),
          assetSuggestions: [
            assetSuggestion(
              "AST-M4S3-SUG-004",
              "media",
              "桂林抗战文化运动地方史料建议",
              "按1938年10月后、1941年1月、1944年冬分层组织桂林与昭平黄姚史料，全文模式必须连续还原C06两段。",
              true,
            ),
            assetSuggestion(
              "AST-M4S3-SUG-005",
              "interface",
              "桂林抗战文化运动连续史料时间轴",
              "以离散节点交互承载C06，但始终提供不删字、不调序的两段连续阅读模式。",
            ),
          ],
          presentation: {
            mode: "local_culture_archive_timeline",
            visualSummary: "在拓展材料标识下，以桂林—昭平黄姚离散档案节点承载C06两段全文。",
            interaction: {
              mode: "continuous_archive_timeline",
              learnerAction:
                "沿时间轴展开组织、统战、文化活动与黄姚办报节点，并随时切换C06全文连续阅读。",
              persistedState: "记录已展开的档案节点，同时保存全文阅读进度与段落顺序。",
              evidenceFeedback: "每个节点显示独立地方史料来源；全文模式逐字按原顺序呈现C06新稿。",
              actualInteraction: true,
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: [
              ...standardGuardrails,
              "明确显示“拓展材料”，不得标作教材正文",
              "不得删减、改写、调序或混入已废止旧稿",
              "桂林与昭平黄姚按离散时空档案切片呈现",
            ],
          },
          sensitivity: "standard",
          reviewState: "pending_review",
          visibleLabel: "拓展材料",
        },
      ],
      extensionLabel: {
        id: "M4S3_GX02_LABEL",
        label: "【广西融入②】",
        visibleLabel: "拓展材料",
        appliesTo: ["M4S3_GX02_C06"],
      },
      gamificationPolicy: "forbidden",
      timing: {
        mainNarrationSeconds: 180,
        extensionNarrationSeconds: 200,
        totalNarrationSeconds: 380,
      },
      reviewState: "pending_review",
    },
  ],
  timing: {
    mainNarrationSeconds: 660,
    extensionNarrationSeconds: 200,
    totalNarrationSeconds: 860,
  },
  reviewState: "pending_review",
  reviewNotes: [
    {
      id: "M4_REVIEW_C05_REPLACEMENT",
      scope: "M4S2知识讲授",
      state: "open",
      differenceFromOriginal:
        "C05以四段确认全文替换原讲授：新增根据地十大政策与延安精神，原干部学校/延安自然科学院段退出。",
      implementationDisposition:
        "蓝图仅从frozenText.m4s2C05按原段落顺序加载，随后进入T13；正式任务数据不在此文件修改。",
    },
    {
      id: "M4_REVIEW_C06_REPLACEMENT",
      scope: "M4S3广西融入②及跨模块删除项",
      state: "open",
      differenceFromOriginal: "C06以两段新稿全文替换原广西融入②，并删除原广西融入③、④。",
      implementationDisposition:
        "M4S3只保留一个②标签和frozenText.gx02C06全文节点；旧②、③、④均为零加载，随后进入T14。",
    },
  ],
};
